/**
 * ColourMatch Pro · Business Profile · Branding tab.
 *
 * Slim, dedicated page for logo and cover image. Uses the same
 * PATCH /api/painter/me endpoint as PainterProfile.jsx — this is a
 * focused UI, not a duplicate editor.
 */
import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, ImagePlus, Loader2 } from "lucide-react";

export default function PainterBranding() {
  const { me, refreshMe } = useOutletContext();
  const [logoUrl, setLogoUrl] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    setLogoUrl(me?.logo_url || "");
    setCoverUrl(me?.cover_image_url || "");
  }, [me]);

  const uploadImage = async (kind, file) => {
    if (!file) return;
    setBusy(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const { data } = await api.post("/painter/me/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (kind === "logo") setLogoUrl(data.url);
      if (kind === "cover") setCoverUrl(data.url);
      await refreshMe?.();
      toast.success(kind === "logo" ? "Logo uploaded" : "Cover image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed. Try a JPG or PNG under 5MB.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="bp-branding">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ImagePicker
          label="Business logo"
          hint="A square logo works best. Shown on presentations, PDFs and your public listing."
          url={logoUrl}
          aspect="aspect-square max-w-[220px]"
          busy={busy === "logo"}
          onPick={(f) => uploadImage("logo", f)}
          testid="branding-logo"
        />
        <ImagePicker
          label="Cover image"
          hint="A wide photo that represents your work. Optional — only used on your public listing."
          url={coverUrl}
          aspect="aspect-[16/9]"
          busy={busy === "cover"}
          onPick={(f) => uploadImage("cover", f)}
          testid="branding-cover"
        />
      </div>
      <p className="text-[11px] text-brand-muted italic max-w-xl leading-relaxed">
        Branding is applied automatically to every presentation and PDF you produce in ColourMatch Pro.
      </p>
    </div>
  );
}

function ImagePicker({ label, hint, url, aspect, busy, onPick, testid }) {
  return (
    <div className="border border-brand-linen bg-white rounded-2xl p-5" data-testid={testid}>
      <div className="text-sm font-semibold text-brand-ink mb-1">{label}</div>
      <div className="text-xs text-brand-muted mb-4">{hint}</div>
      <div className={`relative rounded-xl overflow-hidden bg-brand-linen/40 border border-brand-linen ${aspect}`}>
        {url ? (
          <img src={url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-brand-muted p-6 text-center">
            <ImagePlus className="w-6 h-6 mb-2" />
            <div className="text-xs">No image uploaded</div>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-brand-teal" />
          </div>
        )}
      </div>
      <label className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-brand-linen text-sm text-brand-ink hover:border-brand-teal hover:text-brand-teal transition-colors cursor-pointer">
        <Upload className="w-3.5 h-3.5" />
        {url ? "Replace image" : "Upload image"}
        <input
          type="file"
          accept="image/*,.heic,.heif"
          className="hidden"
          onChange={(e) => onPick(e.target.files?.[0])}
          data-testid={`${testid}-input`}
        />
      </label>
    </div>
  );
}
